/**
 * Query Keys
 *
 * Centralized query key definitions for React Query.
 * Using a factory pattern ensures consistent keys across the application.
 */

export const queryKeys = {
  // Commission ledger (fees earned)
  commissionLedger: {
    all: ['commission_ledger'] as const,
    list: () => [...queryKeys.commissionLedger.all, 'list'] as const,
  },

  // Deals
  deals: {
    all: ['deals'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters ? [...queryKeys.deals.all, 'list', filters] as const : [...queryKeys.deals.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.deals.all, 'detail', id] as const,
    paginated: (page: number, perPage: number) =>
      [...queryKeys.deals.all, 'paginated', { page, perPage }] as const,
  },

  // Clients
  clients: {
    all: ['clients'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters ? [...queryKeys.clients.all, 'list', filters] as const : [...queryKeys.clients.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.clients.all, 'detail', id] as const,
    paginated: (page: number, perPage: number) =>
      [...queryKeys.clients.all, 'paginated', { page, perPage }] as const,
  },

  // Prospects
  prospects: {
    all: ['prospects'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters ? [...queryKeys.prospects.all, 'list', filters] as const : [...queryKeys.prospects.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.prospects.all, 'detail', id] as const,
  },

  // Compliance
  compliance: {
    all: ['compliance'] as const,
    list: () => [...queryKeys.compliance.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.compliance.all, 'detail', id] as const,
  },

  // Contracts
  contracts: {
    all: ['contracts'] as const,
    list: () => [...queryKeys.contracts.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.contracts.all, 'detail', id] as const,
  },

  // Transactions
  transactions: {
    all: ['transactions'] as const,
    list: () => [...queryKeys.transactions.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.transactions.all, 'detail', id] as const,
  },

  // Invoices
  invoices: {
    all: ['invoices'] as const,
    list: () => [...queryKeys.invoices.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.invoices.all, 'detail', id] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    list: () => [...queryKeys.tasks.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.tasks.all, 'detail', id] as const,
    paginated: (page: number, perPage: number, filter?: string) =>
      filter
        ? [...queryKeys.tasks.all, 'paginated', { page, perPage, filter }] as const
        : [...queryKeys.tasks.all, 'paginated', { page, perPage }] as const,
  },

  // Contacts
  contacts: {
    all: ['contacts'] as const,
    list: () => [...queryKeys.contacts.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.contacts.all, 'detail', id] as const,
  },

  // Calendar Events
  events: {
    all: ['events'] as const,
    list: () => [...queryKeys.events.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.events.all, 'detail', id] as const,
  },

  // Notes
  notes: {
    all: ['notes'] as const,
    list: () => [...queryKeys.notes.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.notes.all, 'detail', id] as const,
  },

  // Organizations
  organizations: {
    all: ['organizations'] as const,
    list: () => [...queryKeys.organizations.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.organizations.all, 'detail', id] as const,
  },

  // Activities
  activities: {
    all: ['activities'] as const,
    list: () => [...queryKeys.activities.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.activities.all, 'detail', id] as const,
  },

  // Team Work Summaries
  teamWorkSummaries: {
    all: ['teamWorkSummaries'] as const,
    byAgency: (agencyId: string) => ['teamWorkSummaries', agencyId] as const,
  },

  // Agency Team Members
  agencyTeam: {
    all: ['agencyTeam'] as const,
    members: (agencyId: string) => ['agencyTeam', 'members', agencyId] as const,
    workSummary: (userId: string, agencyId: string) => ['agencyTeam', 'workSummary', userId, agencyId] as const,
    assignmentStats: (agencyId: string) => ['agencyTeam', 'assignmentStats', agencyId] as const,
  },

  // Player Data (live scraper-api data)
  playerData: {
    all: ['playerData'] as const,
    profile: (name: string, league: string) => ['playerData', 'profile', name, league] as const,
    contract: (name: string, sport: string) => ['playerData', 'contract', name, sport] as const,
    injuries: (name: string, league: string) => ['playerData', 'injuries', name, league] as const,
    transactions: (name: string, league: string) => ['playerData', 'transactions', name, league] as const,
    news: (name: string, league: string) => ['playerData', 'news', name, league] as const,
  },
}
